import { GetServerSidePropsContext } from 'next'

import { BASE_PATH_B2B, BIFROST_API, ROUTES } from '../constants'
import { getTargetOrigin } from '../utils/bifrost-config'
import { extractGA4Data } from '../utils/ga4-context'
import { makeRequest, parseJsonResponse } from './api-base'
import type {
  AuthResponse,
  AuthResponseData,
  CompleteSignupPayload,
  CompleteSignupWithCpfPayload,
  ExternalAuthApi,
  LoginPayload,
  RequestPasswordPayload,
  SendSignupOtpPayload,
  SetNewPasswordFromRecoveryTokenPayload,
  SetNewPasswordFromRecoveryTokenResponse,
  SignupPayload,
  ValidateSignupOtpPayload,
  ValidateSignupOtpResponse,
  ValidateSignupOtpResponseData,
} from './types'

function readMeta(name: string): string {
  return document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') || ''
}

function pickCsrfToken(url?: string): string {
  if (url?.startsWith(BIFROST_API.PASSWORD_RECOVERY)) {
    return readMeta('auth-csrf-token') || readMeta('csrf-token')
  }
  if (url?.startsWith(BIFROST_API.SIGNIN.PASSWORD)) {
    const isAuthServer = readMeta('signin-upstream') === 'auth-server'
    return isAuthServer
      ? readMeta('auth-csrf-token') || readMeta('csrf-token')
      : readMeta('csrf-token')
  }
  return readMeta('csrf-token')
}

function getExternalHeaders({ ctx, url }: { ctx?: GetServerSidePropsContext; url?: string } = {}) {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (ctx?.req) {
    headers.Cookie = ctx.req.headers.cookie || ''
    headers['User-Agent'] = ctx.req.headers['user-agent'] || ''
    return headers
  }

  if (typeof window === 'undefined') {
    return headers
  }

  const currentHost = window.location.host
  headers.Referer = window.location.href
  headers.Origin = getTargetOrigin(currentHost)
  const csrfToken = pickCsrfToken(url)

  if (csrfToken) {
    headers['CSRF-Token'] = csrfToken
  }

  return headers
}

async function signInWithEmail({
  email,
  password,
  next_url,
  event_data,
  fingerprint_signals,
}: LoginPayload): Promise<AuthResponse> {
  const ga4Data = extractGA4Data()
  const response = await makeRequest(
    `${BIFROST_API.SIGNIN.PASSWORD}`,
    {
      method: 'POST',
      body: {
        username: email,
        password,
        next_url,
        event_data,
        ...(ga4Data && { ga4_data: JSON.stringify(ga4Data) }),
        ...(fingerprint_signals && { fingerprint_signals }),
      },
    },
    getExternalHeaders
  )
  return parseJsonResponse<AuthResponseData>(response)
}

async function signUpOrSignInByEmail({
  email,
  nextUrl,
  scope,
  event_data,
  allowsOtp,
}: SignupPayload): Promise<AuthResponse> {
  const response = await makeRequest(
    `${BIFROST_API.SIGNUP.BY_EMAIL}`,
    {
      method: 'POST',
      body: {
        email,
        nextUrl,
        event_data,
        ...(scope && { scope }),
        allowsOtp: allowsOtp ?? false,
      },
    },
    getExternalHeaders
  )
  return parseJsonResponse<AuthResponseData>(response)
}

async function sendSignupOtp({ email }: SendSignupOtpPayload): Promise<AuthResponse> {
  const response = await makeRequest(
    `${BIFROST_API.SIGNUP.OTP_SEND}`,
    {
      method: 'POST',
      body: { email },
    },
    getExternalHeaders
  )
  return parseJsonResponse<AuthResponseData>(response)
}

async function validateSignupOtp({
  email,
  otp,
}: ValidateSignupOtpPayload): Promise<ValidateSignupOtpResponse> {
  const response = await makeRequest(
    `${BIFROST_API.SIGNUP.OTP_VALIDATE}`,
    {
      method: 'POST',
      body: { email, otp },
    },
    getExternalHeaders
  )
  return parseJsonResponse<ValidateSignupOtpResponseData>(response)
}

async function requestPasswordRecovery({ email }: RequestPasswordPayload): Promise<AuthResponse> {
  const response = await makeRequest(
    `${BIFROST_API.PASSWORD_RECOVERY}`,
    {
      method: 'POST',
      body: {
        email,
      },
    },
    getExternalHeaders
  )
  return parseJsonResponse<AuthResponseData>(response)
}

async function verifyPasswordRecoveryToken(
  token: string,
  ctx?: GetServerSidePropsContext
): Promise<AuthResponse> {
  const path = `${BIFROST_API.PASSWORD_RECOVERY}/${encodeURIComponent(token)}`
  const url = ctx?.req ? `${getTargetOrigin(ctx.req.headers.host)}${path}` : path
  const response = await makeRequest(url, { method: 'GET', ctx }, getExternalHeaders)
  return parseJsonResponse<AuthResponseData>(response)
}

async function setNewPasswordFromRecoveryToken({
  token,
  password,
}: SetNewPasswordFromRecoveryTokenPayload): Promise<{
  response: Response
  data: SetNewPasswordFromRecoveryTokenResponse | undefined
}> {
  const endpoint = `${BIFROST_API.PASSWORD_RECOVERY}/${encodeURIComponent(token)}`
  const response = await makeRequest(
    `${endpoint}`,
    {
      method: 'POST',
      body: {
        password,
      },
    },
    getExternalHeaders
  )
  return parseJsonResponse<SetNewPasswordFromRecoveryTokenResponse>(response)
}

async function completeSignupWithToken(
  { token, fullName, password, occupation }: CompleteSignupPayload,
  options?: { basePath?: string }
): Promise<AuthResponse> {
  const ga4Data = extractGA4Data()
  const isB2B = options?.basePath === BASE_PATH_B2B
  const path = isB2B
    ? BIFROST_API.SIGNUP.COMPLETE_WITH_TOKEN_B2B
    : BIFROST_API.SIGNUP.COMPLETE_WITH_TOKEN
  const response = await makeRequest(
    `${path}/${encodeURIComponent(token)}`,
    {
      method: 'POST',
      body: {
        name: fullName,
        ...(isB2B
          ? {}
          : { password, occupation, ...(ga4Data && { ga4_data: JSON.stringify(ga4Data) }) }),
      },
    },
    getExternalHeaders
  )
  return parseJsonResponse<AuthResponseData>(response)
}

async function completeSignupWithCpf({
  token,
  cpf,
  birthDate,
  phone,
  whatsAppOptIn,
  event_data,
}: CompleteSignupWithCpfPayload): Promise<AuthResponse> {
  const ga4Data = extractGA4Data()
  const endpoint = ROUTES.SIGN_UP.POST_CPF_BASED + (token ? `/${encodeURIComponent(token)}` : '')
  const response = await makeRequest(
    endpoint,
    {
      method: 'POST',
      body: {
        cpf,
        birthDate,
        ...(phone !== undefined && { phone }),
        ...(whatsAppOptIn !== undefined && {
          whatsAppOptIn: whatsAppOptIn ? 'true' : 'false',
        }),
        ...(event_data && { event_data }),
        ...(ga4Data && { ga4_data: JSON.stringify(ga4Data) }),
      },
    },
    getExternalHeaders
  )
  return parseJsonResponse<AuthResponseData>(response)
}

export const externalAuthApi: ExternalAuthApi = {
  signInWithEmail,
  signUpOrSignInByEmail,
  sendSignupOtp,
  validateSignupOtp,
  requestPasswordRecovery,
  verifyPasswordRecoveryToken,
  setNewPasswordFromRecoveryToken,
  completeSignupWithToken,
  completeSignupWithCpf,
}
